import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { AlertCircle, RefreshCcw, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ComparisonTabErrorBoundaryProps {
    children: React.ReactNode;
    tabName: string;
}

function TabErrorFallback({ error, resetErrorBoundary, tabName }: FallbackProps & { tabName: string }) {
    return (
        <div className="max-w-4xl mx-auto py-12 px-4">
            <Card className="border-destructive/20 shadow-lg bg-white dark:bg-slate-950">
                <CardHeader className="text-center pb-2">
                    <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                        <ShieldAlert className="h-6 w-6 text-destructive" />
                    </div>
                    <CardTitle className="text-2xl font-bold tracking-tight text-foreground">
                        Something went wrong in the {tabName} section
                    </CardTitle>
                    <CardDescription className="text-base text-muted-foreground mt-2">
                        We encountered an unexpected error while rendering this tab. You can try refreshing the tab or navigating to another section.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 flex flex-col items-center pt-4">
                    <div className="w-full p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            <AlertCircle className="h-3 w-3" />
                            Error Details
                        </div>
                        <p className="text-sm font-mono text-slate-700 dark:text-slate-300 break-all">
                            {error.message || 'An unknown error occurred.'}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <Button
                            onClick={resetErrorBoundary}
                            variant="default"
                            size="lg"
                            className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                        >
                            <RefreshCcw className="mr-2 h-4 w-4" />
                            Try Again
                        </Button>
                        <Button
                            onClick={() => window.location.reload()}
                            variant="outline"
                            size="lg"
                        >
                            Reload Page
                        </Button>
                    </div>

                    <p className="text-xs text-muted-foreground">
                        If this problem persists, please contact support with the error details above.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

export const ComparisonTabErrorBoundary: React.FC<ComparisonTabErrorBoundaryProps> = ({
    children,
    tabName
}) => {
    return (
        <ErrorBoundary
            FallbackComponent={(props) => <TabErrorFallback {...props} tabName={tabName} />}
            onError={(error, info) => {
                console.error(`[Comparison Workspace] Error in ${tabName} tab:`, error, info);
            }}
        >
            {children}
        </ErrorBoundary>
    );
};
